import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { api, queryKeys } from "../../api";
import { allowsFeature, useOrgEntitlements } from "../../features/billing/entitlements";
import { authStore } from "../../auth/store";
import { OrgLayout } from "../../components/Shell";
import { ConfirmDialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { SaveBar, settingsEqual } from "../../components/UnsavedChanges";

interface Draft {
  enabled: boolean;
  networks: string[];
  bypass: string[];
}

function draftOf(settings: { ipAllowlistEnabled?: boolean; ipAllowlist?: string[]; ipBypassUserIds?: string[] } | undefined): Draft {
  return {
    enabled: !!settings?.ipAllowlistEnabled,
    networks: settings?.ipAllowlist ?? [],
    bypass: settings?.ipBypassUserIds ?? [],
  };
}

/** A light check so a typo is caught before it is added. The server is authoritative. */
export function normalizeNetwork(raw: string): string | null {
  let s = raw.trim();
  if (!s || /\s/.test(s)) return null;
  if (!s.includes("/")) s += s.includes(":") ? "/128" : "/32";
  const parts = s.split("/");
  if (parts.length !== 2) return null;
  const bits = Number(parts[1]);
  if (!Number.isInteger(bits)) return null;
  const addr = parts[0] ?? "";
  if (addr.includes(":")) {
    if (bits < 0 || bits > 128 || !/^[0-9a-fA-F:]+$/.test(addr)) return null;
    return s;
  }
  const octets = addr.split(".");
  if (bits < 0 || bits > 32 || octets.length !== 4 || octets.some((p) => !/^\d{1,3}$/.test(p) || Number(p) > 255)) return null;
  return s;
}

export function NetworksPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const members = useQuery({ queryKey: queryKeys.members(orgId), queryFn: () => api.orgs.members(orgId) });
  const ent = useOrgEntitlements(orgId);
  const included = allowsFeature(ent.data, "ip_allowlist");
  const [draft, setDraft] = useState<Draft>({ enabled: false, networks: [], bypass: [] });
  const [saved, setSaved] = useState<Draft>(draft);
  const [entry, setEntry] = useState("");
  const [entryError, setEntryError] = useState<string>();
  const [removing, setRemoving] = useState<string | null>(null);
  useEffect(() => {
    if (!org.data) return;
    const next = draftOf(org.data.settings);
    setDraft(next);
    setSaved(next);
  }, [org.data]);

  const missingNetwork = draft.enabled && draft.networks.length === 0;
  const dirty = !settingsEqual(draft, saved);
  const save = useMutation({
    mutationFn: () =>
      api.orgs.patch(orgId, {
        settings: { ipAllowlistEnabled: draft.enabled, ipAllowlist: draft.networks, ipBypassUserIds: draft.bypass },
      }),
    onSuccess: (updated) => {
      const next = draftOf(updated.settings);
      setDraft(next);
      setSaved(next);
      qc.setQueryData(queryKeys.org(orgId), updated);
      void authStore.load();
      toast.push("success", draft.enabled ? "Sign-in networks saved." : "Sign-in is no longer limited by network.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const add = () => {
    const network = normalizeNetwork(entry);
    if (!network) {
      setEntryError("Enter an address or a range, like 203.0.113.10 or 203.0.113.0/24.");
      return;
    }
    setEntry("");
    setEntryError(undefined);
    setDraft((d) => (d.networks.includes(network) ? d : { ...d, networks: [...d.networks, network] }));
  };

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Allowed networks" subtitle="Limit who can sign in by where they are connecting from." />
      {org.isPending || ent.isPending ? (
        <Skeleton />
      ) : ent.isSuccess && !included ? (
        <div className="card section">
          <h2 style={{ marginTop: 0 }}>Available on Business and Enterprise</h2>
          <p className="muted">Sign-in can be limited to chosen networks on the Business and Enterprise plans.</p>
          <Link to="/orgs/$org/billing" params={{ org: orgId }} className="btn primary">
            View plans
          </Link>
        </div>
      ) : org.isError ? (
        <ErrorState error={org.error} retry={() => void org.refetch()} />
      ) : (
        <>
          <div className="card form-card">
            <section className="section">
              <div className="settings-stack">
                <Switch
                  id="limit-networks"
                  checked={draft.enabled}
                  onChange={(enabled) => setDraft((d) => ({ ...d, enabled }))}
                  label="Limit sign-in to these networks"
                  hint="People and API tokens can connect only from a network on this list. Emergency access accounts can still sign in from anywhere, and that sign-in is emailed to the owners."
                />
                <Field label="Network" error={entryError ?? (missingNetwork ? "Add at least one network." : undefined)} hint="An address, or a range such as 203.0.113.0/24.">
                  {(p) => (
                    <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input
                        {...p}
                        className="input"
                        value={entry}
                        placeholder="203.0.113.0/24"
                        onChange={(e) => {
                          setEntry(e.target.value);
                          setEntryError(undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                          }
                        }}
                        style={{ maxWidth: 280 }}
                      />
                      <button type="button" className="btn" disabled={!entry.trim()} onClick={add}>
                        Add network
                      </button>
                    </div>
                  )}
                </Field>
                {draft.networks.length > 0 && (
                  <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {draft.networks.map((network) => (
                      <li key={network} className="row" style={{ justifyContent: "space-between", gap: 12 }}>
                        <span className="mono">{network}</span>
                        <RowMenu label={`Actions for ${network}`}>
                          <button type="button" className="danger" role="menuitem" onClick={() => setRemoving(network)}>
                            Remove network
                          </button>
                        </RowMenu>
                      </li>
                    ))}
                  </ul>
                )}
                <Field label="Emergency access" hint="These accounts can sign in from anywhere if the list locks people out.">
                  {(p) => (
                    <select
                      {...p}
                      className="input"
                      multiple
                      value={draft.bypass}
                      onChange={(e) => setDraft((d) => ({ ...d, bypass: Array.from(e.target.selectedOptions).map((o) => o.value) }))}
                      style={{ height: 120, maxWidth: 420 }}
                    >
                      {(members.data ?? []).map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.email}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
            </section>
          </div>
          <SaveBar
            dirty={dirty}
            saving={save.isPending}
            disabled={missingNetwork}
            onSave={() => save.mutate()}
            onDiscard={() => {
              setDraft(saved);
              setEntry("");
              setEntryError(undefined);
            }}
          />
          <ConfirmDialog
            open={!!removing}
            danger
            title="Remove network"
            confirmLabel="Remove network"
            onClose={() => setRemoving(null)}
            onConfirm={() => {
              setDraft((d) => ({ ...d, networks: d.networks.filter((n) => n !== removing) }));
              setRemoving(null);
            }}
          >
            <p style={{ margin: 0 }}>
              <strong>{removing}</strong> will be removed when you save.
            </p>
          </ConfirmDialog>
        </>
      )}
    </OrgLayout>
  );
}
